// Unpublished Work © 2025-2026 Deere & Company.

import {Config} from './types/config-type';
import {InitialProps, OhDeereProps, Session} from './types/session-type';
import {defaultTranslations} from './translations/default-translations';
import {get} from '@deere/translation-service';
import {redirect} from './helpers/redirect-helper';
import {getLocationHostname} from './helpers/location-helper';
import {setLinks} from './stores/link-store';
import {setOrgFeatureToggles, setUserFeatureToggles} from './stores/toggles-store';
import {setTranslations, freezeStore as freezeTranslationStore} from './stores/translation-store';
import {startDatadog} from './rum/datadog-rum';
import {version} from '../version';
import {ReactionMode} from '@deere/oh-deere-boundary';

type Env = 'qual' | 'cert' | 'prod';

async function getBaseConfig(): Promise<Config> {
    const response = await fetch('/api/config');
    return await response.json();
}

async function getAuthenticatedJson<t>(path: string): Promise<t | undefined> {
    const response = await fetch(path, {
        redirect: 'manual'
    });

    if (response.type === 'opaqueredirect') {
        return undefined;
    }

    return await response.json();
}

function initializeRUM(env: string, userId: string): void {
    try {
        startDatadog(env, userId);
    } catch {
        // ignore errors
    }
}

function initializeSessionVariables(session: Session): void {
    setUserFeatureToggles(session.userToggles);
    setOrgFeatureToggles(session.orgToggles);
    setLinks(session.links);
}

async function getSession(): Promise<Session | undefined> {
    return await getAuthenticatedJson<Session>('/api/session');
}

export function getEnvironmentLinksWhenUnauthenticated(env: Env) {
    const baseUrls = {
        qual: 'https://accountq.tal.deere.com',
        cert: 'https://accountc.deere.com',
        prod: 'https://account.deere.com'
    };
    const targetBaseUrls = {
        qual: 'https://map-qual.deere.com',
        cert: 'https://map-cert.deere.com',
        prod: 'https://map.deere.com'
    };

    const baseUrl = baseUrls[env] || baseUrls.prod;
    const targetBaseUrl = targetBaseUrls[env] || targetBaseUrls.prod;

    return {
        createAccountUrl: `${baseUrl}/actmgmt/onboarding?TARGET=${targetBaseUrl}`
    };
}

export function getEnvFromUrl(hostname: string): Env {
    // Local defaults to qual
    if (hostname === 'local-node-app.jdnet.deere.com') {
        return 'qual';
    }

    // For vanity/deployed URLs ending with qual patterns (e.g., axiomqa.tal.deere.com, operationscenter-qual.deere.com)
    if (
        hostname.includes('-qual.') ||
        hostname.includes('.qual.') ||
        hostname.endsWith('qual.deere.com') ||
        hostname.endsWith('.tal.deere.com')
    ) {
        return 'qual';
    }
    if (hostname.includes('-cert.') || hostname.includes('.cert.') || hostname.endsWith('cert.deere.com')) {
        return 'cert';
    }

    return 'prod';
}

export async function getInitialProps(): Promise<InitialProps> {
    const session = await getSession();

    if (!session) {
        const env = getEnvFromUrl(getLocationHostname());
        setLinks(getEnvironmentLinksWhenUnauthenticated(env));
    }

    try {
        const translations: Record<string, string> = await get('ops-center-router');
        setTranslations(translations);
        freezeTranslationStore();
    } catch {
        setTranslations(defaultTranslations);
        freezeTranslationStore();
    }

    const config = await getBaseConfig();
    const env = config?.environment ?? 'prod';

    if (!session?.hasToken && window.location.pathname !== '/') {
        redirect(`/api/login?next=${encodeURIComponent(window.location.pathname)}`);
    }

    if (session) {
        initializeRUM(env, session.id);
        initializeSessionVariables(session);
    }

    const ohDeereProps: OhDeereProps = {
        env,
        reactionMode: config?.isLocal ? ReactionMode.Nothing : ReactionMode.Redirect,
        service: 'ops-center-router',
        serviceVersion: `git:${version}`
    };

    return {session, ohDeereProps};
}
